import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from "react";
import { isClickOutside } from "../../utils/clickOutside";
import { useWidgetI18n } from "../../context/WidgetI18nContext";
import { ChartTypeMenu } from "./ChartTypeMenu";
import { TopbarCenterCluster } from "./TopbarCenterCluster";
import { TopbarMobilePanel } from "./TopbarMobilePanel";
import { TopbarEnvPopover } from "./TopbarEnvPopover";
import { TopbarRightCluster } from "./TopbarRightCluster";
import { usePortalRoot } from "../../context/VNInvestChartsContext";
import styles from "../../styles/ohlc-header.module.css";
export function OHLCHeader({ symbol, timeframe, chartType = "candlestick", onChartTypeChange, replayPlaying = false, replaySpeed = 1, replayCurrentBar = 0, replayTotalBars = 0, onReplayPlay, onReplayPause, onReplayStop, onReplayStepForward, onReplayStepBack, onReplayRewind, onReplaySpeedChange, onReplaySeek, replayAwaitingStartBar = false, showReplayBar, onShowReplayBarChange, onReplayStartBarSelect, onReplayEnterSelectionMode, onReplayCancelSelection, replayInterval = "source", 
// P2: onReplayIntervalChange đã gỡ (dead — không ai wire, xem FloatingReplayToolbar caption).
// Giữ prop trong interface để tương thích API.
replayTotalPnl, replayAnalytics, showMenu = true, isFullscreen = false, compareOpen = false, onCompareToggle, crosshairMode, onCrosshairModeChange, searchOpen = false, onSearchToggle, onSettingsClick, onFullscreenClick, onSnapshotClick, onCsvExportClick, onAlertsClick, onSaveTemplate, onLoadTemplate, onSystemMenuOpen, showLogo = false, showOHLCV = false, showTicker = true, showVolume = false, showBarChange = true, ohlcData, onTimeframeChange, upgradeUrl, quickTimeframes = ["1m", "5m", "15m", "1h", "1D"], isMobile = false, canUseReplay = true, patTier, drawingWorkspace, theme, onThemePresetChange, }) {
    const { t } = useWidgetI18n();
    const portalRoot = usePortalRoot(); // S9 F-07: shadow portal, fallback body.
    const [showChartTypeDropdown, setShowChartTypeDropdown] = useState(false);
    const [showReplay, setShowReplay] = useState(false);
    const [selectingStartBar, setSelectingStartBar] = useState(false);
    const [systemMenuOpen, setSystemMenuOpen] = useState(false);
    const chartTypeDropdownRef = useRef(null);
    // CLIP-FIX: the menu portals to document.body (fixed) — the header's
    // overflow-x:auto would clip an in-flow absolute menu (state toggled fine,
    // nothing visible => "dropdown doesn't work").
    const chartTypeBtnRef = useRef(null);
    const chartTypeMenuRef = useRef(null);
    const [chartTypeMenuPos, setChartTypeMenuPos] = useState(null);
    const toggleChartTypeDropdown = useCallback(() => {
        setShowChartTypeDropdown((prev) => {
            if (!prev) {
                const r = chartTypeBtnRef.current?.getBoundingClientRect();
                if (r) {
                    const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
                    setChartTypeMenuPos({ top: r.bottom + 4, left: Math.max(0, Math.min(r.left, vw - 160)) });
                }
                else {
                    setChartTypeMenuPos(null);
                }
            }
            return !prev;
        });
    }, []);
    // P2: XÓA REPLAY_INTERVAL_OPTIONS chết (định nghĩa nhưng không render ở đâu;
    // mobile menu không có UI interval). Interval sống ở FloatingReplayToolbar.
    const systemMenuRef = useRef(null);
    // Controlled visibility: parent owns showReplayBar; header mirrors it + notifies parent.
    const setReplayVisible = useCallback((show) => {
        setShowReplay(show);
        onShowReplayBarChange?.(show);
    }, [onShowReplayBarChange]);
    // R1.2: dedicated enter-selection signal — no magic date=0 seek.
    const enterStartBarSelection = useCallback(() => {
        if (onReplayEnterSelectionMode)
            onReplayEnterSelectionMode();
        else
            onReplayStartBarSelect?.(0);
    }, [onReplayEnterSelectionMode, onReplayStartBarSelect]);
    // Reset header internal replay state when floating toolbar is closed externally
    // (parent sets showReplayBar true→false, e.g. toolbar × button or Escape).
    const prevShowReplayBarRef = useRef(showReplayBar);
    useEffect(() => {
        if (prevShowReplayBarRef.current === true && showReplayBar === false) {
            setShowReplay(false);
            setSelectingStartBar(false);
        }
        prevShowReplayBarRef.current = showReplayBar;
    }, [showReplayBar]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Portal menu lives outside the button wrap — check both refs.
            if (isClickOutside(chartTypeDropdownRef.current, event) && isClickOutside(chartTypeMenuRef.current, event)) {
                setShowChartTypeDropdown(false);
            }
            if (systemMenuOpen && systemMenuRef.current && !systemMenuRef.current.contains(event.target)) {
                setSystemMenuOpen(false);
            }
        };
        // A fixed portal detaches from its anchor on scroll/resize — dismiss.
        const handleDismissMenu = () => setShowChartTypeDropdown(false);
        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleDismissMenu, true);
        window.addEventListener("resize", handleDismissMenu);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleDismissMenu, true);
            window.removeEventListener("resize", handleDismissMenu);
        };
    }, [systemMenuOpen]);
    // Hotkeys for replay controls (active only when replay bar is shown)
    useEffect(() => {
        if (!showReplay)
            return;
        const handleKeyDown = (e) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement)
                return;
            switch (e.code) {
                case "Space":
                    e.preventDefault();
                    if (replayPlaying)
                        onReplayPause?.();
                    else
                        onReplayPlay?.();
                    break;
                case "ArrowLeft":
                    e.preventDefault();
                    onReplayStepBack?.();
                    break;
                case "ArrowRight":
                    e.preventDefault();
                    onReplayStepForward?.();
                    break;
                case "KeyR":
                    e.preventDefault();
                    onReplayStop?.();
                    setShowReplay(false);
                    break;
                case "Escape":
                    e.preventDefault();
                    if (selectingStartBar) {
                        setSelectingStartBar(false);
                    }
                    onReplayCancelSelection?.();
                    onReplayStop?.();
                    setShowReplay(false);
                    break;
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [showReplay, replayPlaying, onReplayPlay, onReplayPause, onReplayStop, onReplayStepBack, onReplayStepForward, selectingStartBar]);
    // Exit start-bar selection mode only when parent confirms (prop goes true→false)
    const prevAwaitingRef = useRef(replayAwaitingStartBar);
    useEffect(() => {
        if (prevAwaitingRef.current === true && replayAwaitingStartBar === false) {
            setSelectingStartBar(false);
        }
        prevAwaitingRef.current = replayAwaitingStartBar;
    }, [replayAwaitingStartBar]);
    const replayProgress = replayTotalBars > 0 ? (replayCurrentBar / replayTotalBars) * 100 : 0;
    if (isMobile) {
        return (_jsx(TopbarMobilePanel, { showLogo: showLogo, showTicker: showTicker, symbol: symbol, timeframe: timeframe, onTimeframeChange: onTimeframeChange, quickTimeframes: quickTimeframes, onChartTypeChange: onChartTypeChange, chartType: chartType, showReplay: showReplay, selectingStartBar: selectingStartBar, setSelectingStartBar: setSelectingStartBar, setReplayVisible: setReplayVisible, enterStartBarSelection: enterStartBarSelection, onReplayCancelSelection: onReplayCancelSelection, onReplayStop: onReplayStop, systemMenuOpen: systemMenuOpen, setSystemMenuOpen: setSystemMenuOpen, systemMenuRef: systemMenuRef, onSearchToggle: onSearchToggle, onCompareToggle: onCompareToggle, onSettingsClick: onSettingsClick, onAlertsClick: onAlertsClick, onSnapshotClick: onSnapshotClick, onCsvExportClick: onCsvExportClick, onSaveTemplate: onSaveTemplate, onLoadTemplate: onLoadTemplate, onFullscreenClick: onFullscreenClick, isFullscreen: isFullscreen, onSystemMenuOpen: onSystemMenuOpen, upgradeUrl: upgradeUrl, patTier: patTier, t: t }));
    }
    return (_jsxs("div", { className: styles.ohlcHeader, "data-testid": "ohlc-header", children: [_jsxs("div", { className: styles.leftGroup, children: [showLogo && (_jsx("a", { href: "https://vninvest.edusuccess.vn", target: "_blank", rel: "noopener noreferrer", className: styles.logoBadge, style: { textDecoration: 'none' }, children: "VNINVESTCHARTS" })), showTicker && (_jsx("strong", { className: styles.symbol, children: symbol ?? "—" })), timeframe && (onTimeframeChange ? (_jsx("select", { value: timeframe, onChange: (e) => onTimeframeChange(e.target.value), className: styles.selectorBtn, style: { marginLeft: "6px", cursor: "pointer", fontSize: "11px", padding: "4px 6px" }, "aria-label": t("header.timeframe"), children: quickTimeframes.map(tf => (_jsx("option", { value: tf, children: tf }, tf))) })) : (_jsx("span", { className: styles.timeframe, children: timeframe }))), _jsx(ChartTypeMenu, { open: showChartTypeDropdown, menuPos: chartTypeMenuPos, dropdownRef: chartTypeDropdownRef, btnRef: chartTypeBtnRef, menuRef: chartTypeMenuRef, portalRoot: portalRoot, chartType: chartType, patTier: patTier, upgradeUrl: upgradeUrl, onToggle: toggleChartTypeDropdown, onSelect: (v) => { onChartTypeChange?.(v); setShowChartTypeDropdown(false); }, t: t })] }), _jsx(TopbarCenterCluster, { compareOpen: compareOpen, onCompareToggle: onCompareToggle, crosshairMode: crosshairMode, onCrosshairModeChange: onCrosshairModeChange, canUseReplay: canUseReplay, showReplay: showReplay, selectingStartBar: selectingStartBar, replayPlaying: replayPlaying, setReplayVisible: setReplayVisible, setSelectingStartBar: setSelectingStartBar, enterStartBarSelection: enterStartBarSelection, onReplayCancelSelection: onReplayCancelSelection, onReplayStop: onReplayStop, drawingWorkspace: drawingWorkspace, t: t }), _jsx(TopbarEnvPopover, { patTier: patTier, theme: theme, onThemePresetChange: onThemePresetChange, upgradeUrl: upgradeUrl, portalRoot: portalRoot, t: t }), _jsx(TopbarRightCluster, { showMenu: showMenu, searchOpen: searchOpen, onSearchToggle: onSearchToggle, onSettingsClick: onSettingsClick, isFullscreen: isFullscreen, onFullscreenClick: onFullscreenClick, onSnapshotClick: onSnapshotClick, onCsvExportClick: onCsvExportClick, onSaveTemplate: onSaveTemplate, onLoadTemplate: onLoadTemplate, onAlertsClick: onAlertsClick, onSystemMenuOpen: onSystemMenuOpen, upgradeUrl: upgradeUrl, t: t })] }));
}
